/** Social shares the authenticated room socket through sync-shared.
 * Production supplies sendRaw/onSyncRaw. Isolated benches can omit the transport;
 * presence polling remains available until actual pushes arrive.
 */

import type { SocialClientMessage } from './protocol';
import type { SocialStore, SocialTransport } from './store';

/** Production supplies sendRaw; isolated adapters may lack it. */
export interface RawCapableSync {
  sendRaw?: (msg: unknown) => void;
}

/** The shape of `$lib/player/sync-shared` as far as this file cares. */
interface SyncSharedModule {
  sharedSync: () => RawCapableSync;
  onSyncRaw?: (fn: (msg: unknown) => void) => () => void;
}

let warned = false;

function warnOnce(): void {
  if (warned) return;
  warned = true;
  console.warn(
    '[social] this adapter lacks the shared raw-frame hook; presence polling remains active.'
  );
}

/** Build a transport from a sync engine that already has the hook. Exported for the
 *  bench, which hands it a fake, and for tests. */
export function syncSocialTransport(
  sync: RawCapableSync,
  onRaw: (fn: (msg: unknown) => void) => () => void
): SocialTransport {
  const send = sync.sendRaw?.bind(sync);
  if (send === undefined) throw new Error('this sync engine cannot send raw frames');
  return {
    send: (msg: SocialClientMessage) => send(msg),
    subscribe: (fn) => onRaw(fn)
  };
}

/**
 * Give the store the tab's room socket, if there is a way to reach it.
 *
 * Returns a detach function. Safe to call from every screen that mounts: it is one
 * dynamic import and one capability check, and it deliberately does nothing at all when
 * the capability is absent.
 */
export async function attachSocial(store: SocialStore): Promise<() => void> {
  let mod: SyncSharedModule;
  try {
    mod = (await import('$lib/player/sync-shared')) as unknown as SyncSharedModule;
  } catch {
    // The player bundle is not reachable (a bench page, a test). Nothing to attach.
    return () => undefined;
  }
  if (typeof mod.onSyncRaw !== 'function') {
    warnOnce();
    return () => undefined;
  }
  const transport = syncSocialTransport(mod.sharedSync(), mod.onSyncRaw);
  store.connect(transport);
  return () => store.connect(null);
}
